import { computed, reactive, ref } from "vue";
import { defineStore } from "pinia";
import { useFlash } from "#ce/composables/useFlash";
import { createCanonicalUpdateApi } from "#ce/apps/SpAdmin/SpCanonicalUpdateApp/api/canonical-update";
import { deepMerge, parseJson } from "#ce/apps/SpAdmin/shared/utils/config";
import { pluralizeTemplate } from "#ce/apps/SpAdmin/shared/utils/pluralize";

// Rows the apply will act on; skipped and error rows are display-only.
const ACTIONABLE_STATUSES = ["new", "updated", "removed"];

const SUMMARY_ORDER = ["new", "updated", "removed", "skipped", "unchanged", "errors"];

// The comparison runs in a background job; poll its status every 2.5s, capped
// at 240 consecutive `pending` attempts (~10 minutes) before treating the
// preview as failed. Up to 2 isolated request failures in a row are retried.
export const PREVIEW_POLL_INTERVAL_MS = 2500;
export const PREVIEW_POLL_MAX_ATTEMPTS = 240;
export const PREVIEW_POLL_MAX_TRANSIENT_FAILURES = 3;

function defaultConfig() {
  return {
    previewUrl: "",
    statusUrl: "",
    processUrl: "",
    modalTitle: "Update from Canonical",
    statusLabels: "",
    labels: "{}",
  };
}

// Minimal English defaults so specs and Storybook work standalone; production
// always deep-merges the helper's localized `labels` JSON over these.
function labelDefaults() {
  return {
    confirm: "Apply Updates",
    cancel: "Cancel",
    loading: "Comparing against the canonical source…",
    progress: {
      rows: "%{processed} of %{total} rows compared",
      remaining: "About %{time} remaining",
      minutes: { one: "%{count} minute", other: "%{count} minutes" },
      seconds: { one: "%{count} second", other: "%{count} seconds" },
    },
    emptyState: "Everything is already in sync with the canonical source.",
    noActionableNote: "There is nothing to apply — the rows below are informational only.",
    rejectedNote: "Terms on the rejected-terms sheet are also processed when you apply.",
    removedValue: "Removed: %{name}",
    columns: {
      status: "Status",
      name: "Name",
      type: "Type",
      changes: "Changes",
    },
    attributes: {},
    summary: {
      new: { one: "%{count} new term will be created.", other: "%{count} new terms will be created." },
      updated: { one: "%{count} term will be updated.", other: "%{count} terms will be updated." },
      removed: {
        one: "%{count} term missing from the canonical source will be archived.",
        other: "%{count} terms missing from the canonical source will be archived.",
      },
      skipped: { one: "%{count} term will be skipped.", other: "%{count} terms will be skipped." },
      unchanged: { one: "%{count} term is already in sync.", other: "%{count} terms are already in sync." },
      errors: { one: "%{count} row has errors.", other: "%{count} rows have errors." },
    },
    errors: {
      previewFailed: "The comparison against the canonical source could not be completed.",
      processFailed: "The update could not be queued.",
    },
    flash: {
      queued: "The update from the canonical source has been queued. Terms will be updated in the background.",
    },
  };
}

function emptySummary() {
  return { new: 0, updated: 0, unchanged: 0, skipped: 0, removed: 0, errors: 0, total: 0 };
}

// setTimeout-based (not setInterval) so specs can drive the poll loop with
// fake timers one tick at a time.
function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export const useCanonicalUpdateStore = defineStore("canonicalUpdate", () => {
  const api = createCanonicalUpdateApi();
  const flash = useFlash();

  const config = reactive(defaultConfig());

  // Guards against stale async responses: an in-flight preview/confirm started
  // before reset()/close() (or superseded by a newer request) must not apply
  // its response over newer state. Non-reactive — only compared, never rendered.
  let requestGeneration = 0;

  // STATE:
  const isOpen = ref(false);
  const isLoading = ref(false);
  const previewData = ref(null);
  const previewToken = ref("");
  const isSubmitting = ref(false);
  const errorMessage = ref("");
  // { processed, total, estimatedSecondsRemaining } while the background comparison
  // reports row progress; null before the job starts its row loop.
  const previewProgress = ref(null);

  // COMPUTED:
  const statusLabels = computed(() => ({
    new: "New term, will be created",
    updated: "Existing term, will be updated",
    removed: "Missing from the canonical source, will be archived",
    skipped: "Will be skipped",
    unchanged: "Already in sync, nothing will change",
    ...parseJson(config.statusLabels, {}),
  }));
  // Localized UI copy from the helper, deep-merged over the English defaults.
  const labels = computed(() => deepMerge(labelDefaults(), parseJson(config.labels, {})));
  const summary = computed(() => ({ ...emptySummary(), ...(previewData.value?.summary ?? {}) }));
  const rows = computed(() => previewData.value?.rows ?? []);
  const actionableRows = computed(() => rows.value.filter((row) => ACTIONABLE_STATUSES.includes(row.status)));
  const requestInFlight = computed(() => isLoading.value || isSubmitting.value);
  const confirmDisabled = computed(() => requestInFlight.value || actionableRows.value.length === 0);
  // Summary fragments composed from the server-provided pluralization
  // templates; zero counts stay out of the sentence.
  const summaryParts = computed(() => {
    const templates = labels.value.summary;

    return SUMMARY_ORDER.filter((key) => summary.value[key] > 0).map((key) =>
      pluralizeTemplate(templates[key], summary.value[key]),
    );
  });
  const summaryText = computed(() => summaryParts.value.join(" "));
  const progressPercent = computed(() => {
    const progress = previewProgress.value;
    if (!progress || progress.total <= 0) {
      return null;
    }

    return Math.min(100, Math.round((progress.processed / progress.total) * 100));
  });
  const progressText = computed(() => {
    const progress = previewProgress.value;
    if (!progress || progress.total <= 0) {
      return "";
    }

    return labels.value.progress.rows
      .replaceAll("%{processed}", progress.processed)
      .replaceAll("%{total}", progress.total);
  });
  // "About X minutes remaining", from the server's linear estimate; rounds to whole
  // minutes above 90s so the countdown reads calmly instead of ticking seconds.
  const remainingText = computed(() => {
    const seconds = previewProgress.value?.estimatedSecondsRemaining;
    if (seconds === null || seconds === undefined || seconds <= 0) {
      return "";
    }

    const templates = labels.value.progress;
    let time;
    if (seconds >= 90) {
      time = pluralizeTemplate(templates.minutes, Math.round(seconds / 60));
    } else {
      time = pluralizeTemplate(templates.seconds, Math.round(seconds));
    }

    return templates.remaining.replaceAll("%{time}", time);
  });

  // ACTIONS:
  function configure(nextConfig = {}) {
    Object.assign(config, defaultConfig(), nextConfig);
  }

  function reset() {
    requestGeneration += 1;
    previewData.value = null;
    previewToken.value = "";
    isLoading.value = false;
    isSubmitting.value = false;
    errorMessage.value = "";
    previewProgress.value = null;
  }

  // There is no input step: opening immediately fetches the preview.
  function open() {
    reset();
    isOpen.value = true;

    return preview();
  }

  function close() {
    isOpen.value = false;
    reset();
  }

  function applyPreviewData(data) {
    previewData.value = {
      rows: data.rows || [],
      summary: { ...emptySummary(), ...(data.summary || {}) },
      errors: data.errors || [],
    };
  }

  async function preview() {
    requestGeneration += 1;
    const generation = requestGeneration;
    isLoading.value = true;
    errorMessage.value = "";
    previewToken.value = "";

    try {
      const started = await api.preview(config.previewUrl);

      if (generation !== requestGeneration) {
        return false;
      }

      // A 422 start returns the rows/summary payload directly (top-level
      // errors) instead of a token — render it without polling.
      if (Array.isArray(started?.rows)) {
        applyPreviewData(started);
        return true;
      }

      if (!started?.token) {
        errorMessage.value = labels.value.errors.previewFailed;
        return false;
      }

      previewToken.value = started.token;
      return await pollPreviewStatus(generation);
    } catch (error) {
      if (generation !== requestGeneration) {
        return false;
      }

      errorMessage.value = error.message || labels.value.errors.previewFailed;
      return false;
    } finally {
      // A stale request must not clear a newer request's loading state.
      if (generation === requestGeneration) {
        isLoading.value = false;
      }
    }
  }

  // Polls the status endpoint until the background comparison is ready, failed,
  // or expired. The generation guard covers the whole loop: a poll response
  // landing after close() or a newer preview is discarded, not applied.
  // A `running` response resets the attempt cap — the server is demonstrably
  // working and its token TTL bounds the total wait — so the cap only counts
  // consecutive silent `pending` ticks. Isolated request failures are retried:
  // a lone 502 must not kill a comparison still running server-side.
  async function pollPreviewStatus(generation) {
    let attempt = 0;
    let transientFailures = 0;

    while (attempt < PREVIEW_POLL_MAX_ATTEMPTS) {
      attempt += 1;
      await sleep(PREVIEW_POLL_INTERVAL_MS);

      if (generation !== requestGeneration) {
        return false;
      }

      let data;
      try {
        data = await api.previewStatus(config.statusUrl, previewToken.value);
      } catch (error) {
        if (generation !== requestGeneration) {
          return false;
        }

        transientFailures += 1;
        if (transientFailures >= PREVIEW_POLL_MAX_TRANSIENT_FAILURES) {
          throw error;
        }

        continue;
      }

      transientFailures = 0;

      if (generation !== requestGeneration) {
        return false;
      }

      if (data?.status === "ready") {
        applyPreviewData(data);
        return true;
      }

      // The dry runs report row progress once the job reaches its row loop.
      if (data?.status === "running") {
        attempt = 0;
        previewProgress.value = {
          processed: data.processed ?? 0,
          total: data.total ?? 0,
          estimatedSecondsRemaining: data.estimated_seconds_remaining ?? null,
        };
        continue;
      }

      // failed/expired (or an unrecognized status): surface the server errors
      // when present, the generic preview failure otherwise.
      if (data?.status !== "pending") {
        errorMessage.value = (data?.errors ?? []).join(", ") || labels.value.errors.previewFailed;
        return false;
      }
    }

    // Poll cap reached (~10 minutes without the job starting): treat as failed.
    errorMessage.value = labels.value.errors.previewFailed;
    return false;
  }

  async function confirm() {
    requestGeneration += 1;
    const generation = requestGeneration;
    isSubmitting.value = true;
    errorMessage.value = "";

    try {
      const data = await api.process(config.processUrl, { token: previewToken.value });

      if (generation !== requestGeneration) {
        return false;
      }

      // queued:false includes an expired preview token (the review sat open
      // too long, or was already applied); the modal stays open so the user
      // can close and re-open to re-preview.
      if (!data?.queued) {
        errorMessage.value = (data?.errors ?? []).join(", ") || labels.value.errors.processFailed;
        return false;
      }

      // The apply runs in the background, so the page keeps showing pre-update
      // data either way — no reload, just the queued acknowledgement (a
      // client-side <sp-alert> a reload would destroy).
      flash.success(labels.value.flash.queued, { autoremoveDelay: 8000 });
      close();
      return true;
    } catch (error) {
      if (generation !== requestGeneration) {
        return false;
      }

      errorMessage.value = error.message || labels.value.errors.processFailed;
      return false;
    } finally {
      // A stale request must not clear a newer request's submitting state. (The
      // success path bumps the generation itself via close() → reset(), which
      // also already reset isSubmitting.)
      if (generation === requestGeneration) {
        isSubmitting.value = false;
      }
    }
  }

  return {
    config,
    isOpen,
    isLoading,
    previewData,
    previewToken,
    isSubmitting,
    errorMessage,
    previewProgress,
    statusLabels,
    labels,
    summary,
    rows,
    actionableRows,
    requestInFlight,
    confirmDisabled,
    summaryParts,
    summaryText,
    progressPercent,
    progressText,
    remainingText,
    configure,
    reset,
    open,
    close,
    preview,
    confirm,
  };
});
